import { gsap } from 'gsap';
import { useState, useRef, useMemo, useEffect } from 'react';

import type { ProductImageGallery2 } from '~/amplienceTypes/schemas/imported/product-image-gallery-schema';
import { useMediaQueryContext } from '~/utilities/context/dynamic/MediaQueryContext';
import { usePDPContext } from '~/utilities/context/static/PDPContext';
import { VideoMode } from '~/utilities/graphql/codegen';

interface UseCarouselStateProps extends Pick<ProductImageGallery2, 'autoSlide'> {}

export const useCarouselState = ({ autoSlide }: UseCarouselStateProps) => {
  const { isDesktop } = useMediaQueryContext();
  const {
    product: { videos, code, shouldUseDarkBackgroundImgs = true },
  } = usePDPContext();

  // State management
  const [videoActive, setVideoActive] = useState<boolean>(true);
  const [timerStopped, setTimerStopped] = useState<boolean>(false);
  const [scrollInProgress, setScrollInProgress] = useState<number[]>([]);
  const [autoplay, setAutoplay] = useState(true);
  const [isSwipeMode, setIsSwipeMode] = useState(false);

  // Refs
  const galleryRef = useRef<HTMLUListElement>(null);
  const itemRefs = useRef<(HTMLLIElement | null)[]>([]);
  const progressBarRef = useRef<HTMLDivElement>(null);

  // Computed values
  const video = videos?.find(v => v?.mode === VideoMode.Landscape);
  const gsapQuery = gsap.utils.selector(galleryRef);

  const isAutoSlide = useMemo(
    () => (!isDesktop && (autoSlide?.mobile || autoSlide?.tablet)) || false,
    [autoSlide?.mobile, autoSlide?.tablet, isDesktop]
  );

  useEffect(() => {
    setIsSwipeMode(!isDesktop);
  }, [isDesktop]);

  return {
    // State
    videoActive,
    setVideoActive,
    timerStopped,
    stopTimer: setTimerStopped,
    scrollInProgress,
    setScrollInProgress,
    autoplay,
    setAutoplay,
    isSwipeMode,
    setIsSwipeMode,

    // Refs
    galleryRef,
    itemRefs,
    progressBarRef,

    // Computed
    video,
    gsapQuery,
    isAutoSlide,
    isMobile: !isDesktop,

    // Product data
    code,
    shouldUseDarkBackgroundImgs,
  };
};
